/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { ElementRef, Inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { ActivationStart, NavigationStart, Router } from '@angular/router';
import {
	ApiService,
	AppSettingsService,
	CustomWindow,
	enumKeys,
	FeatureService,
	HistoryService,
	HttpArgs,
	InjectScriptService,
	isNewPageOrSearchQuery,
	isSearchPage,
	isSortOrFiltersChanged,
	ModalRoutingService,
	PortalService,
	StatefulService,
	WINDOW,
} from '@woolworthsnz/styleguide';
import { ContextResponse, DynamicContentRequest } from '@woolworthsnz/trader-api';
import { map, take, tap } from 'rxjs';
import { BannerComponent } from '../components/banner/banner.component';
import { DynamicContentComponent } from '../components';
import { ShellService } from '@woolworthsnz/shop';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpParams } from '@angular/common/http';

export class ContentState {
	headerBanners: any[];
	shelfBanners: any[];
	dynamicContentSecondaryPanel: any;
	dynamicContentPanel: any;
	dynamicContentBottomPanel: any;
	dynamicContentTitlePanel: any;
	dynamicContentGlobalPanel: any;
	dynamicContentDetailsPanel: any;
}

export class BannerTarget {
	skus?: string[];
	searchString?: string;
	departmentId?: number;
	aisleId?: number;
	shelfId?: number;
	productGroup?: string;
	url?: string;
	targetContext?: DynamicContentRequest.TargetContextEnum;
}

export enum DynamicContentTargets {
	dynamicContentSecondaryPanel = 'dynamic-content-secondary-panel',
	dynamicContentPanel = 'dynamic-content-panel',
	dynamicContentBottomPanel = 'dynamic-content-bottom-panel',
	dynamicContentTitlePanel = 'dynamic-content-title-panel',
	dynamicContentDetailsPanel = 'dynamic-content-details-panel',
	dynamicContentGlobalPanel = 'dynamic-content-global-panel',
	dynamicContentHeadertBanner = 'header-banner',
	dynamicContentShelfBanner = 'shelf-banner',
}

interface PanelDef {
	hostId: string;
	key: keyof ContentState;
	element?: ElementRef;
}

@Injectable({
	providedIn: 'root',
})
export class DynamicContentService extends StatefulService<ContentState> {
	private document: Document;
	private panels: PanelDef[] = [];
	private renderer: Renderer2;
	private prevPath: string | undefined = undefined;
	private isCartologyGAMSessionGroup = false;

	constructor(
		@Inject(WINDOW) private window: CustomWindow,
		private appSettingsService: AppSettingsService,
		private apiService: ApiService,
		private portalService: PortalService,
		private router: Router,
		private modalRoutingService: ModalRoutingService,
		private historyService: HistoryService,
		private shellService: ShellService,
		private injectScriptService: InjectScriptService,
		rendererFactory: RendererFactory2,
		private featureService: FeatureService
	) {
		super(new ContentState());
		this.document = window.document;
		this.renderer = rendererFactory.createRenderer(null, null);

		this.isCartologyGAMSessionGroup = this.featureService.isFeatureEnabled(
			ContextResponse.EnabledFeaturesEnum.CartologyGAMSessionGroup
		);

		for (const key of enumKeys(DynamicContentTargets)) {
			this.panels.push(<PanelDef>{ hostId: DynamicContentTargets[key], key });
		}

		this.shellService.state$
			.pipe(
				takeUntilDestroyed(),
				map((state) => state.contentEditor),
				tap((isContentEditor) => this.initDynamicContentEditor(isContentEditor))
			)
			.subscribe();

		this.router.events.pipe(takeUntilDestroyed()).subscribe((routerEvent) => {
			if (routerEvent instanceof ActivationStart) {
				this.handleActivationStart(routerEvent);
			}

			if (routerEvent instanceof NavigationStart) {
				// passing true to the exit will just reload the window without any confirmation
				// currently there's no easy way to show a confirmation when navigating away
				// as angular will not stop the navigation based on the output,
				// it also will crash the router observable
				if (this.window.dc?.mode === 'edit') {
					this.window.dc.exit(true);
				}
			}
		});
	}

	get dynamicContentEndpoint() {
		return this.appSettingsService.getEndpoint('dynamicContent', 2);
	}

	get headerBanners() {
		return this.state && this.state.headerBanners;
	}

	get shelfBanners() {
		return this.state && this.state.shelfBanners;
	}

	private handleActivationStart(routerEvent: ActivationStart): void {
		let currPath = '';

		if (routerEvent.snapshot.url?.length > 0) {
			currPath = `/${routerEvent.snapshot.url.map((x) => x.path).join('/')}`;
		}

		if (routerEvent.snapshot.queryParams) {
			const currParams = new HttpParams({ fromObject: routerEvent.snapshot.queryParams }).toString();
			currPath = `${currPath}?${currParams}`;
		}
		// The ActivationStart event can be triggered multiple times upon a route change, for example when you're navigating to a child route (as is the case for modals)
		// We then want to ignore the ActivationStart event that is triggered for the parent route, which would otherwise clear the dynamic content.
		if (currPath === this.prevPath) {
			return;
		}

		// We want to skip clearing dynamic content when a modal opens.
		const isModal = this.modalRoutingService.isInModal(
			this.appSettingsService.getSetting('modalOutletName'),
			routerEvent.snapshot
		);

		if (!isModal) {
			this.prevPath = currPath;
		}

		// We want to hide dynamic content for a search-kind page plus if not the first page
		const previousUrl = this.historyService.getPreviousUrl();
		const currentUrl = this.router.getCurrentNavigation()?.finalUrl?.toString() ?? '';
		let stayInCurrentPosition = isSearchPage(currentUrl) && !isNewPageOrSearchQuery(previousUrl, currentUrl);
		if (this.isCartologyGAMSessionGroup) {
			stayInCurrentPosition = stayInCurrentPosition && !isSortOrFiltersChanged(previousUrl, currentUrl);
		}

		if (!isModal && !stayInCurrentPosition) {
			// clear dynamic content from portal and destroy components.
			this.panels.forEach((panel: PanelDef) => this.clearPanel(panel));

			// remove the edit buttons for editors as they get recreated when needed
			// when the dynamic content is rendered during renderPanel
			this.window.dc?.removeEditButtons();
		}
	}

	init(args: BannerTarget) {
		const httpArgs: HttpArgs = {
			params: { ...this.buildBannerTargetInformation(args) },
		};

		return this.apiService
			.get(this.dynamicContentEndpoint, httpArgs)
			.pipe(take(1))
			.subscribe((response) => {
				this.updateAttributes(args.url || httpArgs.params.url, args.skus);

				if (response) {
					this.setState(response);
					this.renderDynamicContent();
					this.renderBannerPanels();
				}
			});
	}

	buildBannerTargetInformation(args: BannerTarget): BannerTarget {
		return <BannerTarget>{
			url: this.router.url.replace(/\?.*/g, ''),
			...args,
		};
	}

	renderPanel(panelDef: PanelDef): void {
		let host;
		let idName;

		if (panelDef.element) {
			host = panelDef.element;
			idName = panelDef.key;
		} else {
			host = panelDef.hostId;
			idName = panelDef.key;
		}

		if (!host || !this.state[panelDef.key]) {
			return;
		}

		this.portalService.initDomPortal(host, DynamicContentComponent, this.state[panelDef.key], idName);
	}

	clearPanel(panel: PanelDef) {
		const key = panel.element ? panel.key : panel.hostId;
		this.portalService.clearDomPortal(key);
	}

	renderBannerPanels() {
		const headerBanners = this.headerBanners && this.headerBanners.length && this.headerBanners;
		const shelfBanners = this.shelfBanners && this.shelfBanners.length && this.shelfBanners;

		// A requirement from legacy is to not show headerBanners if shelfBanners are present
		const banners = shelfBanners || headerBanners;
		const position = shelfBanners ? 'shelf' : 'header';

		this.portalService.initDomPortal('header-banner', BannerComponent, { banners, position });
	}

	renderShelfBannerPanels() {
		if (!this.shelfBanners || !this.shelfBanners.length) {
			return;
		}

		this.portalService.initDomPortal('shelf-banner', BannerComponent, this.shelfBanners);
	}

	setViewChildForOutlet(target: ElementRef, name: string) {
		const targetPanel = this.panels.find((panel) => panel.hostId === name);
		if (targetPanel) {
			targetPanel.element = target;
		}
	}

	clearViewChildForOutlet(name: string) {
		const targetPanel = this.panels.find((panel) => panel.hostId === name);
		if (targetPanel) {
			targetPanel.element = undefined;
		}
	}

	renderDynamicContent() {
		this.panels.forEach((panelDef: PanelDef) => this.renderPanel(panelDef));
		this.appSettingsService.isDynamicConentShown.set(true);
		this.window.dc?.createEditButtons();
	}

	initDynamicContentEditor(isEditor: boolean | undefined) {
		if (!isEditor || this.window.dc) {
			return;
		}

		const scriptLocation = this.appSettingsService.getScripts('dynamicContentEditor');

		this.injectScriptService.injectScript(this.document, scriptLocation, () => {
			// show the edit buttons once the dc editor has loaded for the first time
			// the script usually loads after init so the edit buttons won't appear
			this.window.dc.createEditButtons();
		});
	}

	private updateAttributes(url: string, skus?: string[]): void {
		let sku = '';
		if (skus?.length) {
			sku = skus[0];
		}

		this.panels.forEach((panelDef) => {
			const container = this.document?.getElementById(panelDef.hostId);

			if (container) {
				// adds schedule key (dynamic content path) to the panel
				// so the dc editor knows which area to open / edit
				this.renderer.setAttribute(
					container,
					'data-schedule-key',
					`${url}${this.getUrlPostfixForPanel(panelDef.key, sku)}` || ''
				);
			}
		});

		this.window.dc?.createEditButtons();
	}

	private getUrlPostfixForPanel(key: keyof ContentState, sku: string) {
		// each panel's schedule key is postfixed based on the type
		// i.e. recipe's secondary panel will be "/recipes_2"
		switch (key) {
			case 'dynamicContentBottomPanel':
				return '_bottom';
			case 'dynamicContentSecondaryPanel':
				return '_2';
			case 'dynamicContentGlobalPanel':
				return `_${sku}_Global`;
			case 'dynamicContentTitlePanel':
				return `_${sku}_Title`;
			case 'dynamicContentDetailsPanel':
				return `_${sku}_Details`;
			default:
				return '';
		}
	}
}
